import React, { useEffect, useMemo, useState } from "react";
import "./0admin.css";

// Types
interface TokenResponse {
  access_token: string;
  token_type: string;
}

type UserStatus = "PROGRESS" | "ACTIVE" | "HOLD";

interface UserItem {
  id: number;
  mail?: string;
  name?: string;
  surname?: string;
  department_id?: number | null;
  status: UserStatus;
}

interface UsersListResponse {
  items: UserItem[];
  total: number;
  limit: number;
  offset: number;
}

interface SqlExecuteResult {
  columns?: string[];
  rows?: any[];
  rowCount?: number;
  notice?: string;
  error?: string;
}

// Requisites
interface RequisiteOut {
  id: number;
  requisite_name: string;
  requisite_details: string;
}
interface RequisitesList { items: RequisiteOut[] }

// Categories & Departments
interface CategoryOut { id: number; category: string }
interface DepartmentOut { id: number; department: string; category_id?: number | null; category_name?: string | null }

// Helper to get/set token
const TOKEN_KEY = "admin_token";
const getToken = () => sessionStorage.getItem(TOKEN_KEY) || "";
const setToken = (t: string) => sessionStorage.setItem(TOKEN_KEY, t);
const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);

// Backend base URL
const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/,'');

// API helper
async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init && init.headers ? (init.headers as any) : {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers,
  });
  if (res.status === 401 || res.status === 403) {
    throw new Error("Unauthorized");
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  // fallback
  const text = await res.text();
  return JSON.parse(text) as T;
}

const AdminPage = () => {
  // Auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setTokenState] = useState<string>(getToken());
  const [authError, setAuthError] = useState<string>("");
  const loggedIn = !!token;

  // Users pane state
  const [status, setStatus] = useState<UserStatus>("PROGRESS");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [workingIds, setWorkingIds] = useState<Record<number, boolean>>({});

  // Edit modal state
  const [editTarget, setEditTarget] = useState<UserItem | null>(null);
  const [editName, setEditName] = useState("");
  const [editSurname, setEditSurname] = useState("");
  const [editDepartment, setEditDepartment] = useState<string>("");

  // SQL pane state
  const [sqlText, setSqlText] = useState("SELECT id, mail, name, surname, status FROM public.users LIMIT 10;");
  const [sqlResult, setSqlResult] = useState<SqlExecuteResult | null>(null);
  const [sqlRunning, setSqlRunning] = useState(false);

  // Requisites state
  const [reqs, setReqs] = useState<RequisiteOut[]>([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqError, setReqError] = useState<string>("");
  const [reqCreateOpen, setReqCreateOpen] = useState(false);
  const [reqNewName, setReqNewName] = useState("");
  const [reqNewDetails, setReqNewDetails] = useState("");
  const [reqEditId, setReqEditId] = useState<number | null>(null);
  const [reqEditName, setReqEditName] = useState("");
  const [reqEditDetails, setReqEditDetails] = useState("");

  // Admin Tools: categories/departments state
  const [cats, setCats] = useState<CategoryOut[]>([]);
  const [deps, setDeps] = useState<DepartmentOut[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [depLoading, setDepLoading] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newDep, setNewDep] = useState("");
  const [newDepCat, setNewDepCat] = useState<string>("");

  // API Diagnostic Tool state
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagUrl, setDiagUrl] = useState("/api/profiles/me");
  const [diagMethod, setDiagMethod] = useState("GET");
  const [diagBody, setDiagBody] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState<{status: number; statusText: string; headers: Record<string, string>; body: any} | null>(null);
  const [diagError, setDiagError] = useState<string>("");

  // Effects
  useEffect(() => {
    if (!loggedIn) return;
    void loadUsers();
    void loadRequisites();
    void loadCategories();
    void loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, offset, limit, loggedIn]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthError("");
    try {
      const body = new URLSearchParams();
      body.append("username", email);
      body.append("password", password);

      const res = await fetch(`${API_BASE}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) {
        let detail = "Login failed";
        try { const j = await res.json(); detail = j?.detail || detail; } catch {}
        throw new Error(detail);
      }
      const data = (await res.json()) as TokenResponse;
      setToken(data.access_token);
      setTokenState(data.access_token);
      setEmail("");
      setPassword("");
      setOffset(0);
    } catch (err: any) {
      setAuthError(err?.message || "Login error");
    }
  }

  function handleLogout() {
    clearToken();
    setTokenState("");
    setUsers([]);
    setSqlResult(null);
  }

  async function loadUsers() {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      if (search) params.set("search", search);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const data = await apiFetch<UsersListResponse>(`/admin/users?${params.toString()}`);
      setUsers(data.items);
      setTotal(data.total);
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      console.error(e);
    } finally {
      setLoadingUsers(false);
    }
  }

  function refresh() {
    setOffset(0);
    void loadUsers();
    void loadRequisites();
    void loadCategories();
    void loadDepartments();
  }

  async function onAction(userId: number, action: "accept" | "hold" | "delete") {
    setWorkingIds((m) => ({ ...m, [userId]: true }));
    try {
      if (action === "delete") {
        if (!confirm("Delete user permanently?")) return;
        await apiFetch(`/admin/users/${userId}`, { method: "DELETE" });
      } else if (action === "accept") {
        await apiFetch(`/admin/users/${userId}/accept`, { method: "POST" });
      } else if (action === "hold") {
        await apiFetch(`/admin/users/${userId}/hold`, { method: "POST" });
      }
      await loadUsers();
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Action failed");
    } finally {
      setWorkingIds((m) => ({ ...m, [userId]: false }));
    }
  }

  function openEdit(u: UserItem) {
    setEditTarget(u);
    setEditName(u.name || "");
    setEditSurname(u.surname || "");
    setEditDepartment(u.department_id != null ? String(u.department_id) : "");
  }

  async function saveEdit() {
    if (!editTarget) return;
    const body: any = {};
    if (editName !== (editTarget.name || "")) body.name = editName;
    if (editSurname !== (editTarget.surname || "")) body.surname = editSurname;
    if (editDepartment !== (editTarget.department_id != null ? String(editTarget.department_id) : ""))
      body.department = editDepartment ? Number(editDepartment) : null;
    if (Object.keys(body).length === 0) {
      setEditTarget(null);
      return;
    }
    try {
      await apiFetch(`/admin/users/${editTarget.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setEditTarget(null);
      await loadUsers();
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Update failed");
    }
  }

  async function runSql() {
    setSqlRunning(true);
    setSqlResult(null);
    try {
      const data = await apiFetch<SqlExecuteResult>("/admin/sql/execute", {
        method: "POST",
        body: JSON.stringify({ query: sqlText }),
      });
      setSqlResult(data);
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      setSqlResult({ error: (e as Error).message });
    } finally {
      setSqlRunning(false);
    }
  }

  // ===== Requisites API helpers =====
  async function loadRequisites() {
    setReqLoading(true); setReqError("");
    try {
      const data = await apiFetch<RequisitesList>("/admin/requisites");
      setReqs(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      setReqError((e as Error).message || "Failed to load requisites");
    } finally {
      setReqLoading(false);
    }
  }

  async function createRequisite() {
    if (!reqNewName.trim()) { alert("Name is required"); return; }
    try {
      await apiFetch<RequisiteOut>("/admin/requisites", {
        method: "POST",
        body: JSON.stringify({ requisite_name: reqNewName, requisite_details: reqNewDetails })
      });
      setReqNewName(""); setReqNewDetails(""); setReqCreateOpen(false);
      await loadRequisites();
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Create failed");
    }
  }

  function beginEditRequisite(r: RequisiteOut) {
    setReqEditId(r.id); setReqEditName(r.requisite_name); setReqEditDetails(r.requisite_details);
  }
  function cancelEditRequisite() { setReqEditId(null); setReqEditName(""); setReqEditDetails(""); }

  async function saveEditRequisite() {
    if (reqEditId == null) return;
    if (!reqEditName.trim()) { alert("Name is required"); return; }
    try {
      await apiFetch<RequisiteOut>(`/admin/requisites/${reqEditId}`, {
        method: "PATCH",
        body: JSON.stringify({ requisite_name: reqEditName, requisite_details: reqEditDetails })
      });
      cancelEditRequisite();
      await loadRequisites();
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Update failed");
    }
  }

  async function deleteRequisite(id: number) {
    if (!confirm("Delete this requisite?")) return;
    try {
      await apiFetch(`/admin/requisites/${id}`, { method: "DELETE" });
      await loadRequisites();
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Delete failed");
    }
  }

  // ===== Admin Tools helpers =====
  async function downloadBackup() {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/admin/db/backup`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        let msg = `Backup failed (${res.status})`;
        try { const j = await res.json(); msg = (j as any)?.detail || (j as any)?.error || msg; } catch {}
        alert(msg);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match?.[1] || `db_backup_${new Date().toISOString().replace(/[:.-]/g,'')}.sql`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e:any) {
      alert(e?.message || 'Backup error');
    }
  }

  async function loadCategories() {
    setCatLoading(true);
    try {
      const data = await apiFetch<{ items: CategoryOut[] }>(`/admin/categories`);
      setCats(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
    } finally { setCatLoading(false); }
  }

  async function loadDepartments() {
    setDepLoading(true);
    try {
      const data = await apiFetch<{ items: DepartmentOut[] }>(`/admin/departments`);
      setDeps(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      if ((e as Error).message === "Unauthorized") handleLogout();
    } finally { setDepLoading(false); }
  }

  async function createCategoryApi() {
    if (!newCat.trim()) { alert("Category name is required"); return; }
    try {
      await apiFetch(`/admin/categories`, { method: 'POST', body: JSON.stringify({ category: newCat.trim() }) });
      setNewCat("");
      await loadCategories();
    } catch (e:any) {
      if (e?.message === "Unauthorized") handleLogout();
      alert("Create category failed");
    }
  }

  async function deleteCategoryApi(id: number) {
    if (!confirm("Delete this category? It must have no departments.")) return;
    try {
      await apiFetch(`/admin/categories/${id}`, { method: 'DELETE' });
      await loadCategories();
      await loadDepartments();
    } catch (e:any) {
      if (e?.message === "Unauthorized") handleLogout();
      alert("Delete category failed. Ensure no departments are assigned.");
    }
  }

  async function createDepartmentApi() {
    if (!newDep.trim()) { alert("Department name is required"); return; }
    const catId = newDepCat ? Number(newDepCat) : null;
    try {
      await apiFetch(`/admin/departments`, { method: 'POST', body: JSON.stringify({ department: newDep.trim(), category: catId }) });
      setNewDep(""); setNewDepCat("");
      await loadDepartments();
    } catch (e:any) {
      if (e?.message === "Unauthorized") handleLogout();
      alert("Create department failed");
    }
  }

async function deleteDepartmentApi(id: number) {
    if (!confirm("Delete this department?")) return;
    try {
      await apiFetch(`/admin/departments/${id}`, { method: 'DELETE' });
      await loadDepartments();
    } catch (e:any) {
      if ((e as Error).message === "Unauthorized") handleLogout();
      alert("Delete department failed");
    }
  }

  // ===== API Diagnostic Tool =====
  async function runDiag() {
    setDiagLoading(true);
    setDiagResult(null);
    setDiagError("");
    try {
      const url = diagUrl.startsWith("http") ? diagUrl : `${API_BASE}${diagUrl}`;
      const options: RequestInit = {
        method: diagMethod,
        headers: {
          "Content-Type": "application/json",
          ...(diagMethod !== "GET" && diagMethod !== "HEAD" && diagBody ? { "Body": diagBody } : {}),
        },
        credentials: "include",
      };
      if (diagMethod !== "GET" && diagMethod !== "HEAD" && diagBody) {
        options.body = diagBody;
      }
      const res = await fetch(url, options);
      const headers: Record<string, string> = {};
      res.headers.forEach((v, k) => { headers[k] = v; });
      let body: any;
      const ct = res.headers.get("content-type") || "";
      try {
        if (ct.includes("application/json")) {
          body = await res.json();
        } else {
          body = await res.text();
        }
      } catch {
        body = "(non-text response)";
      }
      setDiagResult({
        status: res.status,
        statusText: res.statusText,
        headers,
        body,
      });
    } catch (e: any) {
      setDiagError(e?.message || "Request failed");
    } finally {
      setDiagLoading(false);
    }
  }

  const totalPages = useMemo(() => Math.ceil(total / Math.max(1, limit)), [total, limit]);
  const currentPage = useMemo(() => Math.floor(offset / Math.max(1, limit)) + 1, [offset, limit]);

  return (
    <div className="admin-root">
      {!loggedIn ? (
        <div className="admin-login-panel">
          <form onSubmit={handleLogin} className="admin-login-form">
            <div>
              <label>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
            </div>
            <div>
              <label>Password</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required />
            </div>
            <button className="admin-btn admin-btn--primary" type="submit">Login</button>
            {authError ? <div className="admin-error-box">{authError}</div> : null}
          </form>
        </div>
      ) : (
        <>
          <header className="admin-topbar">
            <h1>Admin</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="admin-btn" onClick={() => setDiagOpen(true)}>API Diagnostic</button>
              <button className="admin-btn" onClick={handleLogout}>Logout</button>
            </div>
          </header>

          <div className="admin-grid">
          <section className="admin-pane left">
            <div className="admin-pane__header">
              <div className="admin-filters">
                <select value={status} onChange={(e) => { setStatus(e.target.value as UserStatus); setOffset(0); }}>
                  <option value="PROGRESS">PROGRESS</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="HOLD">HOLD</option>
                </select>
                <input
                  placeholder="Search name/surname/email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="admin-btn" onClick={refresh} disabled={loadingUsers}>Refresh</button>
              </div>
            </div>
            <div className="admin-pane__body">
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Surname</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.mail || ""}</td>
                        <td>{u.name || ""}</td>
                        <td>{u.surname || ""}</td>
                        <td>{u.department_id ?? ""}</td>
                        <td>{u.status}</td>
                        <td className="admin-actions">
                          <button className="admin-btn" disabled={!!workingIds[u.id]} onClick={() => onAction(u.id, "accept")}>Accept</button>
                          <button className="admin-btn" disabled={!!workingIds[u.id]} onClick={() => onAction(u.id, "hold")}>Hold</button>
                          <button className="admin-btn admin-btn--danger" disabled={!!workingIds[u.id]} onClick={() => onAction(u.id, "delete")}>Delete</button>
                          <button className="admin-btn" onClick={() => openEdit(u)}>Edit</button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", color: "#999" }}>{loadingUsers ? "Loading..." : "No users"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="admin-pane__footer">
              <div className="admin-pagination">
                <button className="admin-btn" disabled={offset === 0 || loadingUsers} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
                <span>
                  Page {total ? currentPage : 0} / {Math.max(1, totalPages)}
                </span>
                <button className="admin-btn" disabled={offset + limit >= total || loadingUsers} onClick={() => setOffset(offset + limit)}>Next</button>
                <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </section>

          <section className="admin-pane right">
            <div className="admin-pane__header">
              <h3>SQL Console</h3>
            </div>
            <div className="admin-pane__body">
              <textarea className="admin-sql-input" value={sqlText} onChange={(e) => setSqlText(e.target.value)} />
              <div>
                <button className="admin-btn admin-btn--primary" onClick={runSql} disabled={sqlRunning}>Run</button>
              </div>
              <div className="admin-result">
                {sqlResult?.error ? (
                  <div className="admin-error-box">{sqlResult.error}</div>
                ) : sqlResult?.columns && sqlResult?.rows ? (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          {sqlResult.columns.map((c) => (
                            <th key={c}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sqlResult.rows.map((r, idx) => (
                          <tr key={idx}>
                            {sqlResult.columns!.map((c) => (
                              <td key={c}>{String(r[c])}</td>
                            ))}
                          </tr>
                        ))}
                        {sqlResult.rows.length === 0 && (
                          <tr><td colSpan={sqlResult.columns.length} style={{ textAlign: "center", color: "#999" }}>No rows</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : sqlResult?.rowCount != null ? (
                  <div className="admin-notice">Affected rows: {sqlResult.rowCount}</div>
                ) : null}
              </div>
            </div>
          </section>

          <section className="admin-pane right">
            <div className="admin-pane__header">
              <h3>Admin Tools</h3>
              <div>
                <button className="admin-btn" onClick={downloadBackup}>Download DB Backup</button>
              </div>
            </div>
            <div className="admin-pane__body">
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                <div>
                  <h4 style={{margin:'6px 0'}}>Categories</h4>
                  <div className="admin-form-grid" style={{marginBottom: 8}}>
                    <label>Name</label>
                    <input value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="New category" />
                    <div style={{gridColumn:'1 / -1', display:'flex', gap:8, justifyContent:'flex-end'}}>
                      <button className="admin-btn" onClick={()=>setNewCat("")}>Clear</button>
                      <button className="admin-btn admin-btn--primary" onClick={createCategoryApi}>Create</button>
                    </div>
                  </div>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr><th style={{width:70}}>ID</th><th>Name</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {cats.map(c=> (
                          <tr key={c.id}>
                            <td>{c.id}</td>
                            <td>{c.category}</td>
                            <td className="admin-actions">
                              <button className="admin-btn admin-btn--danger" onClick={()=>deleteCategoryApi(c.id)} disabled={catLoading}>Delete</button>
                            </td>
                          </tr>
                        ))}
                        {cats.length===0 && (
                          <tr><td colSpan={3} style={{textAlign:'center', color:'#999'}}>{catLoading ? 'Loading…' : 'No categories'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h4 style={{margin:'6px 0'}}>Departments</h4>
                  <div className="admin-form-grid" style={{marginBottom: 8}}>
                    <label>Name</label>
                    <input value={newDep} onChange={e=>setNewDep(e.target.value)} placeholder="New department" />
                    <label>Category</label>
                    <select value={newDepCat} onChange={e=>setNewDepCat(e.target.value)}>
                      <option value="">(none)</option>
                      {cats.map(c=> <option key={c.id} value={String(c.id)}>{c.category}</option>)}
                    </select>
                    <div style={{gridColumn:'1 / -1', display:'flex', gap:8, justifyContent:'flex-end'}}>
                      <button className="admin-btn" onClick={()=>{ setNewDep(""); setNewDepCat(""); }}>Clear</button>
                      <button className="admin-btn admin-btn--primary" onClick={createDepartmentApi}>Create</button>
                    </div>
                  </div>
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr><th style={{width:70}}>ID</th><th>Department</th><th>Category</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {deps.map(d=> (
                          <tr key={d.id}>
                            <td>{d.id}</td>
                            <td>{d.department}</td>
                            <td>{d.category_name || ''}</td>
                            <td className="admin-actions">
                              <button className="admin-btn admin-btn--danger" onClick={()=>deleteDepartmentApi(d.id)} disabled={depLoading}>Delete</button>
                            </td>
                          </tr>
                        ))}
                        {deps.length===0 && (
                          <tr><td colSpan={4} style={{textAlign:'center', color:'#999'}}>{depLoading ? 'Loading…' : 'No departments'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="admin-pane right">
            <div className="admin-pane__header">
              <h3>Requisites</h3>
              <div>
                <button className="admin-btn" onClick={() => setReqCreateOpen(v=>!v)}>{reqCreateOpen ? "Close" : "Add New"}</button>
                <button className="admin-btn" onClick={loadRequisites} disabled={reqLoading}>Refresh</button>
              </div>
            </div>
            <div className="admin-pane__body">
              {reqError && <div className="admin-error-box">{reqError}</div>}
              {reqCreateOpen && (
                <div className="admin-form-grid" style={{marginBottom: 12}}>
                  <label>Name</label>
                  <input value={reqNewName} onChange={(e)=>setReqNewName(e.target.value)} placeholder="Requisite name" />
                  <label>Details</label>
                  <textarea value={reqNewDetails} onChange={(e)=>setReqNewDetails(e.target.value)} placeholder="Multiline details" rows={6} style={{whiteSpace:'pre-wrap'}} />
                  <div style={{gridColumn:'1 / -1', display:'flex', gap:8, justifyContent:'flex-end'}}>
                    <button className="admin-btn" onClick={()=>{ setReqNewName(""); setReqNewDetails(""); }}>Clear</button>
                    <button className="admin-btn admin-btn--primary" onClick={createRequisite}>Create</button>
                  </div>
                </div>
              )}

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{width:70}}>ID</th>
                      <th>Name</th>
                      <th>Details</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reqs.map((r)=> (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td>
                          {reqEditId === r.id ? (
                            <input value={reqEditName} onChange={(e)=>setReqEditName(e.target.value)} />
                          ) : r.requisite_name}
                        </td>
                        <td>
                          {reqEditId === r.id ? (
                            <textarea value={reqEditDetails} onChange={(e)=>setReqEditDetails(e.target.value)} rows={6} style={{whiteSpace:'pre-wrap', width:'100%'}} />
                          ) : (
                            <div style={{whiteSpace:'pre-wrap'}}>{r.requisite_details}</div>
                          )}
                        </td>
                        <td className="admin-actions">
                          {reqEditId === r.id ? (
                            <>
                              <button className="admin-btn" onClick={cancelEditRequisite}>Cancel</button>
                              <button className="admin-btn admin-btn--primary" onClick={saveEditRequisite}>Save</button>
                            </>
                          ) : (
                            <>
                              <button className="admin-btn" onClick={()=>beginEditRequisite(r)}>Edit</button>
                              <button className="admin-btn admin-btn--danger" onClick={()=>deleteRequisite(r.id)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {reqs.length === 0 && (
                      <tr><td colSpan={4} style={{textAlign:'center', color:'#999'}}>{reqLoading ? 'Loading…' : 'No requisites'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
</section>
          </div>
        </>
      )}

      {editTarget && (
        <div className="admin-modal-overlay" onClick={() => setEditTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User</h3>
            <div className="admin-form-grid">
              <label>Name</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} />
              <label>Surname</label>
              <input value={editSurname} onChange={(e) => setEditSurname(e.target.value)} />
              <label>Department</label>
              <input value={editDepartment} onChange={(e) => setEditDepartment(e.target.value)} placeholder="department_id" />
            </div>
            <div className="modal-actions">
              <button className="admin-btn" onClick={() => setEditTarget(null)}>Cancel</button>
              <button className="admin-btn admin-btn--primary" onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {diagOpen && (
        <div className="admin-modal-overlay" onClick={() => setDiagOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>API Diagnostic Tool</h3>
              <button className="admin-btn" onClick={() => setDiagOpen(false)}>Close</button>
            </div>
            <div className="admin-form-grid" style={{ gap: 12 }}>
              <label style={{ gridColumn: '1 / 2' }}>Method</label>
              <select value={diagMethod} onChange={e => setDiagMethod(e.target.value)} style={{ gridColumn: '2 / 3' }}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
              <label style={{ gridColumn: '1 / 2' }}>Endpoint URL</label>
              <input
                value={diagUrl}
                onChange={e => setDiagUrl(e.target.value)}
                placeholder="/api/profiles/me"
                style={{ gridColumn: '2 / 3' }}
              />
              {(diagMethod === "POST" || diagMethod === "PUT" || diagMethod === "PATCH") && (
                <>
                  <label style={{ gridColumn: '1 / 2' }}>Body (JSON)</label>
                  <textarea
                    value={diagBody}
                    onChange={e => setDiagBody(e.target.value)}
                    placeholder='{"key": "value"}'
                    rows={4}
                    style={{ gridColumn: '2 / 3', fontFamily: 'monospace' }}
                  />
                </>
              )}
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="admin-btn" onClick={() => setDiagUrl("/health")}>Test /health</button>
                <button className="admin-btn" onClick={() => setDiagUrl("/api/profiles/me")}>Test /api/profiles/me</button>
                <button className="admin-btn" onClick={() => setDiagUrl("/login/check-identifier")}>Test /login/check-identifier</button>
                <button className="admin-btn admin-btn--primary" onClick={runDiag} disabled={diagLoading}>
                  {diagLoading ? "Running..." : "Send Request"}
                </button>
              </div>
            </div>
            {diagError && <div className="admin-error-box" style={{ marginTop: 12 }}>{diagError}</div>}
            {diagResult && (
              <div style={{ marginTop: 16, borderTop: '1px solid #ddd', paddingTop: 12 }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                  <strong>Status:</strong>
                  <span style={{ color: diagResult.status >= 200 && diagResult.status < 300 ? 'green' : 'red' }}>
                    {diagResult.status} {diagResult.statusText}
                  </span>
                </div>
                {Object.keys(diagResult.headers).length > 0 && (
                  <details style={{ marginBottom: 8 }}>
                    <summary style={{ cursor: 'pointer', color: '#666' }}>Response Headers</summary>
                    <pre style={{ fontSize: 11, background: '#f5f5f5', padding: 8, overflow: 'auto', maxHeight: 150 }}>
                      {JSON.stringify(diagResult.headers, null, 2)}
                    </pre>
                  </details>
                )}
                <strong>Response Body:</strong>
                <pre style={{ fontSize: 11, background: '#f5f5f5', padding: 8, overflow: 'auto', maxHeight: 300, marginTop: 4 }}>
                  {typeof diagResult.body === 'object' ? JSON.stringify(diagResult.body, null, 2) : String(diagResult.body)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
